import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * Searchable select aligned with MUI form controls (same surface, borders, popper as date/text fields).
 */
const SearchableDropdown = ({
  options = [],
  onSelect,
  selectedOption,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  displayKey = 'name',
  secondaryDisplayKey,
  className = '',
  onSearchChange,
  searchValue,
  headerContent,
  renderOption,
  filterOptions: filterOptionsProp,
  isLoading = false,
  /**
   * When true (default), the field behaves like a native select: the closed value isn’t editable/selectable;
   * typing filters only while the list is open. Set false for a always-typable combobox.
   */
  readOnlyWhenClosed = true,
}) => {
  const usesCustomFilter = typeof filterOptionsProp === 'function';
  const controlledFilter =
    !usesCustomFilter &&
    searchValue !== undefined &&
    typeof onSearchChange === 'function';

  const [menuOpen, setMenuOpen] = useState(false);
  /** Same as menuOpen, updated synchronously in open/close handlers (avoids one-frame gaps with controlled input). */
  const menuOpenRef = useRef(false);

  const optionLabel = useMemo(() => {
    if (selectedOption == null) return '';
    const v = selectedOption[displayKey];
    return v != null ? String(v) : '';
  }, [selectedOption, displayKey]);

  const [internalQuery, setInternalQuery] = useState(() =>
    controlledFilter ? '' : optionLabel
  );

  /** While closed, force input text to match the selection (Autocomplete can still drift to “free text”). */
  useEffect(() => {
    if (!readOnlyWhenClosed || menuOpen || controlledFilter) return;
    if (internalQuery !== optionLabel) {
      setInternalQuery(optionLabel);
    }
  }, [readOnlyWhenClosed, menuOpen, controlledFilter, internalQuery, optionLabel]);

  const inputValue = controlledFilter ? (searchValue ?? '') : internalQuery;

  const handleInputChange = (_event, newValue, reason) => {
    if (
      readOnlyWhenClosed &&
      !menuOpenRef.current &&
      (reason === 'input' || reason === 'clear')
    ) {
      return;
    }
    if (reason === 'input' || reason === 'clear' || reason === 'reset') {
      if (controlledFilter) {
        onSearchChange(newValue ?? '');
      } else {
        setInternalQuery(newValue ?? '');
      }
    }
  };

  const resolvedFilterOptions = useMemo(() => {
    if (usesCustomFilter) return filterOptionsProp;
    if (controlledFilter) return (opts) => opts;
    return createFilterOptions({
      stringify: (option) => {
        const bits = [
          option[displayKey],
          secondaryDisplayKey ? option[secondaryDisplayKey] : '',
        ]
          .filter((x) => x != null && x !== '')
          .map(String);
        return bits.join(' ');
      },
    });
  }, [
    usesCustomFilter,
    filterOptionsProp,
    controlledFilter,
    displayKey,
    secondaryDisplayKey,
  ]);

  const ListboxComponent = useMemo(() => {
    if (!headerContent) return undefined;
    return forwardRef(function ListboxWithHeader(props, ref) {
      const { children, ...other } = props;
      return (
        <ul ref={ref} {...other}>
          <Box
            component="li"
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              display: 'block',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {headerContent}
          </Box>
          {children}
        </ul>
      );
    });
  }, [headerContent]);

  const defaultRenderOption = (option) => (
    <Box>
      <Box sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
        {option[displayKey]}
      </Box>
      {secondaryDisplayKey &&
        option[secondaryDisplayKey] != null &&
        option[secondaryDisplayKey] !== 'all' && (
          <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
            {option[secondaryDisplayKey]}
          </Box>
        )}
    </Box>
  );

  const inputLocked = readOnlyWhenClosed && !menuOpen;

  const syncMenuOpen = (next) => {
    menuOpenRef.current = next;
    setMenuOpen(next);
  };

  return (
    <Autocomplete
      className={className}
      fullWidth
      size="small"
      disableClearable
      options={options}
      value={selectedOption ?? null}
      {...(readOnlyWhenClosed ? { open: menuOpen } : {})}
      onOpen={() => syncMenuOpen(true)}
      onClose={() => syncMenuOpen(false)}
      onChange={(_e, newVal) => {
        if (newVal) onSelect(newVal);
      }}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      selectOnFocus={!readOnlyWhenClosed}
      filterOptions={resolvedFilterOptions}
      getOptionLabel={(option) => {
        if (option == null) return '';
        const v = option[displayKey];
        return v != null ? String(v) : '';
      }}
      isOptionEqualToValue={(a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.id != null && b.id != null) return a.id === b.id;
        if (a.value != null && b.value != null) return a.value === b.value;
        return false;
      }}
      ListboxComponent={ListboxComponent}
      ListboxProps={{
        'aria-label': searchPlaceholder,
      }}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'offset',
              options: { offset: [0, 6] },
            },
          ],
        },
        paper: {
          sx: {
            mt: 0.5,
          },
        },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box
            component="li"
            key={option.id ?? option.value ?? key}
            {...rest}
            sx={{
              display: 'block !important',
              alignItems: 'stretch',
            }}
          >
            {renderOption ? renderOption(option) : defaultRenderOption(option)}
          </Box>
        );
      }}
      renderInput={(params) => {
        const {
          onChange: inputOnChange,
          onKeyDown: inputOnKeyDown,
          onPaste: inputOnPaste,
          onCut: inputOnCut,
          onCompositionStart: inputOnCompositionStart,
          onCompositionEnd: inputOnCompositionEnd,
          onInput: inputOnInput,
          ...restInputProps
        } = params.inputProps;
        return (
          <TextField
            {...params}
            placeholder={placeholder}
            sx={
              readOnlyWhenClosed
                ? {
                    '& .MuiOutlinedInput-input': {
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      cursor: menuOpen ? 'text' : 'pointer',
                      ...(inputLocked
                        ? {
                            caretColor: 'transparent',
                            WebkitTextFillColor: 'inherit',
                          }
                        : {}),
                    },
                  }
                : undefined
            }
            InputProps={{
              ...params.InputProps,
              readOnly: inputLocked,
              sx: {
                pr: 1,
                ...(readOnlyWhenClosed ? { cursor: menuOpen ? 'text' : 'pointer' } : {}),
              },
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...restInputProps,
              readOnly: inputLocked,
              inputMode: inputLocked ? 'none' : 'text',
              'aria-label': placeholder,
              onChange: (e) => {
                if (readOnlyWhenClosed && !menuOpenRef.current) {
                  e.preventDefault();
                  return;
                }
                inputOnChange?.(e);
              },
              onKeyDown: (e) => {
                inputOnKeyDown?.(e);
                if (
                  e.defaultPrevented ||
                  !readOnlyWhenClosed ||
                  menuOpenRef.current
                ) {
                  return;
                }
                const allow =
                  e.key === 'Tab' ||
                  e.key === 'Escape' ||
                  e.key === 'Enter' ||
                  e.key.startsWith('Arrow') ||
                  e.key === 'Home' ||
                  e.key === 'End' ||
                  e.key === 'PageUp' ||
                  e.key === 'PageDown' ||
                  e.metaKey ||
                  e.ctrlKey;
                if (!allow) {
                  e.preventDefault();
                }
              },
              onPaste: (e) => {
                if (readOnlyWhenClosed && !menuOpenRef.current) {
                  e.preventDefault();
                  return;
                }
                inputOnPaste?.(e);
              },
              onCut: (e) => {
                if (readOnlyWhenClosed && !menuOpenRef.current) {
                  e.preventDefault();
                  return;
                }
                inputOnCut?.(e);
              },
              onCompositionStart: (e) => {
                if (readOnlyWhenClosed && !menuOpenRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                inputOnCompositionStart?.(e);
              },
              onCompositionEnd: (e) => {
                inputOnCompositionEnd?.(e);
                if (
                  readOnlyWhenClosed &&
                  !menuOpenRef.current &&
                  !controlledFilter
                ) {
                  setInternalQuery(optionLabel);
                }
              },
              onInput: (e) => {
                if (readOnlyWhenClosed && !menuOpenRef.current) {
                  e.preventDefault();
                  if (!controlledFilter) {
                    setInternalQuery(optionLabel);
                  }
                  return;
                }
                inputOnInput?.(e);
              },
            }}
          />
        );
      }}
    />
  );
};

export default SearchableDropdown;
